/**
 * Private Route Component
 *
 * Protects routes that require authentication
 * Redirects to login if user is not authenticated
 */

import { ComponentChildren } from 'preact';
import { route } from 'preact-router';
import { useAuth } from '../../hooks/useAuth';
import { useEffect } from 'preact/hooks';

interface PrivateRouteProps {
  children: ComponentChildren;
  path?: string;
}

export function PrivateRoute({ children }: PrivateRouteProps) {
  const { isAuthenticated, loading } = useAuth();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      // Redirect to login if not authenticated
      route('/login', true);
    }
  }, [isAuthenticated, loading]);

  // Show loading state while verifying authentication
  if (loading) {
    return (
      <div class="flex items-center justify-center min-h-screen">
        <div class="text-center">
          <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p class="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // Show nothing if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  // Render protected content
  return <>{children}</>;
}

/**
 * Private Route with Status Check
 *
 * Protects routes that require specific user status
 */

interface StatusProtectedRouteProps {
  children: ComponentChildren;
  allowedStatuses: Array<'registered' | 'pending_approval' | 'approved_pending_payment' | 'active' | 'rejected' | 'inactive'>;
  redirectTo?: string;
}

export function StatusProtectedRoute({
  children,
  allowedStatuses,
  redirectTo = '/'
}: StatusProtectedRouteProps) {
  const { user, loading, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated) {
        route('/login', true);
        return;
      }

      if (user && !allowedStatuses.includes(user.status)) {
        route(redirectTo, true);
      }
    }
  }, [isAuthenticated, loading, user, allowedStatuses, redirectTo]);

  // Show loading state
  if (loading) {
    return (
      <div class="flex items-center justify-center min-h-screen">
        <div class="text-center">
          <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p class="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  // Show nothing if not authorized (will redirect)
  if (!isAuthenticated || (user && !allowedStatuses.includes(user.status))) {
    return null;
  }

  // Render protected content
  return <>{children}</>;
}
